import type { CourseDraftPayload } from '../services/api'

export type GridPoint = { x: number; y: number }

export const normalizeGridCorners = (points: GridPoint[]): GridPoint[] => {
  if (points.length !== 4) return points
  const remaining = [...points]
  const take = (score: (point: GridPoint) => number) => {
    const index = remaining.reduce((best, point, current) => score(point) < score(remaining[best]) ? current : best, 0)
    return remaining.splice(index, 1)[0]
  }
  const topLeft = take(point => point.x + point.y)
  const bottomRight = take(point => -(point.x + point.y))
  const [first, second] = remaining
  const topRight = first.x - first.y > second.x - second.y ? first : second
  const bottomLeft = topRight === first ? second : first
  return [topLeft, topRight, bottomLeft, bottomRight]
}

export const populatedCells = (value: CourseDraftPayload) => value.holes.reduce((total, hole) =>
  total + (hole.par == null ? 0 : 1) + (hole.handicap == null ? 0 : 1) + Object.values(hole.distances).filter(distance => distance != null).length, 0)

export const detectedHoleRange = (value: CourseDraftPayload): 'front-nine'|'back-nine'|'eighteen'|'other' => {
  const numbers = [...new Set(value.holes.map(hole => hole.number))].sort((a, b) => a - b)
  if (numbers.length === 18 && numbers.every((number, index) => number === index + 1)) return 'eighteen'
  if (numbers.length === 9 && numbers.every((number, index) => number === index + 1)) return 'front-nine'
  if (numbers.length === 9 && numbers.every((number, index) => number === index + 10)) return 'back-nine'
  return 'other'
}

export const mergeWithoutReplacing = (current: CourseDraftPayload, incoming: CourseDraftPayload): CourseDraftPayload => {
  const byNumber = new Map(current.holes.map(hole => [hole.number, hole]))
  incoming.holes.forEach(hole => {
    const existing = byNumber.get(hole.number)
    const distances = { ...hole.distances }
    if (existing) Object.entries(existing.distances).forEach(([tee, distance]) => { if (distance != null || !(tee in distances)) distances[tee] = distance })
    byNumber.set(hole.number, existing ? { ...hole, par: existing.par ?? hole.par, handicap: existing.handicap ?? hole.handicap, distances } : hole)
  })
  return { ...incoming, courseName: current.courseName || incoming.courseName, location: current.location || incoming.location,
    holes: [...byNumber.values()].sort((a, b) => a.number - b.number) }
}
